import Image from 'next/image'
import React from 'react'

/**
 * 支付卡片容器组件
 * 提供统一的支付页面布局和样式
 */
interface PaymentCardProps {
  children: React.ReactNode
  className?: string
}

export function PaymentCard({ children, className = '' }: PaymentCardProps) {
  return (
    <div className={`bg-white rounded-xl shadow-lg overflow-hidden ${className}`}>
      {children}
    </div>
  )
}

/**
 * 支付状态头部组件
 * 显示支付金额和状态信息
 */
interface PaymentHeaderProps {
  amount: string
  desc: string
  status?: string
  statusText?: string
}

export function PaymentHeader({ amount, desc, status, statusText }: PaymentHeaderProps) {
  return (
    <div className="bg-white px-6 py-6 text-center">
      {statusText && (
        <p className="text-gray-600 text-sm mb-4">{statusText}</p>
      )}
      <div className="mb-6">
        <p className="text-gray-600 text-sm mb-2">{desc}</p>
        <p className="text-3xl font-bold text-gray-900">¥{amount || '0.00'}</p>
      </div>
      {status && (
        <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
          {status}
        </div>
      )}
    </div>
  )
}

/**
 * 支付方式选择组件
 * 支持单选和只读模式
 */
interface PaymentMethodOption {
  id: string
  name: string
  description: string
  icon: string
  color: string
  recommended?: boolean
}

interface PaymentMethodsProps {
  methods: PaymentMethodOption[]
  selectedMethod?: string
  onSelect?: (methodId: string) => void
  readOnly?: boolean
}

export function PaymentMethods({
                                 methods,
                                 selectedMethod,
                                 onSelect,
                                 readOnly = false,
                               }: PaymentMethodsProps) {
  return (
    <div className="px-6 flex flex-col">
      <h3 className="text-lg font-medium text-gray-900 mb-4 flex-shrink-0">
        {readOnly ? '支付方式' : '选择支付方式'}
      </h3>

      <div className="space-y-3 flex-1">
        {methods.map((method) => (
          <div
            key={method.id}
            className={`border rounded-lg p-4 transition-colors ${
              readOnly
                ? 'border-gray-200 bg-gray-50'
                : 'cursor-pointer hover:border-gray-300'
            } ${
              selectedMethod === method.id
                ? `border-${method.color}-500 bg-${method.color}-50`
                : 'border-gray-200'
            }`}
            onClick={() => !readOnly && onSelect?.(method.id)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div
                  className={`w-10 h-10 bg-${method.color}-500 rounded-lg flex items-center justify-center mr-3 overflow-hidden relative`}>
                  {method.icon.startsWith('/') ? (
                    <Image
                      src={method.icon}
                      alt={method.name}
                      fill
                    />
                  ) : (
                    <span className="text-white text-lg font-bold">{method.icon}</span>
                  )}
                </div>
                <div>
                  <div className="flex items-center">
                    <span className="font-medium text-gray-900">{method.name}</span>
                    {method.recommended && (
                      <span className="ml-2 px-2 py-1 bg-orange-100 text-orange-600 text-xs rounded">推荐</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500">{method.description}</p>
                </div>
              </div>
              {!readOnly && (
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  selectedMethod === method.id
                    ? `border-${method.color}-500 bg-${method.color}-500`
                    : 'border-gray-300'
                }`}>
                  {selectedMethod === method.id && (
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * 支付按钮组件
 * 支持不同状态和样式
 */
interface PaymentButtonProps {
  onClick: (e?: React.MouseEvent | React.FormEvent) => void
  disabled?: boolean
  loading?: boolean
  text: string
  paymentMethod?: string
  amount?: string
  className?: string
}

export function PaymentButton({
                                onClick,
                                disabled = false,
                                loading = false,
                                text,
                                paymentMethod,
                                amount,
                                className = '',
                              }: PaymentButtonProps) {
  const getButtonColor = () => {
    switch (paymentMethod) {
      case 'alipay':
        return 'bg-blue-500 hover:bg-blue-600 focus:ring-blue-500'
      case 'wechat':
        return 'bg-green-500 hover:bg-green-600 focus:ring-green-500'
      case 'unionpay':
        return 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-600'
      default:
        return 'bg-blue-500 hover:bg-blue-600 focus:ring-blue-500'
    }
  }

  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      className={`w-full py-4 px-4 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-lg text-white ${getButtonColor()} ${className}`}
    >
      {loading ? '处理中...' : text}
    </button>
  )
}

/**
 * 状态指示器组件
 * 显示支付状态和相关信息
 */
interface StatusIndicatorProps {
  status: 'PENDING' | 'SUCCESS' | 'FAILED' | 'CLOSED'
  message?: string
  showIcon?: boolean
}

export function StatusIndicator({ status, message, showIcon = true }: StatusIndicatorProps) {
  const getStatusConfig = () => {
    switch (status) {
      case 'SUCCESS':
        return {
          icon: '✅',
          text: '支付成功',
          className: 'bg-green-100 text-green-800',
        }
      case 'FAILED':
        return {
          icon: '❌',
          text: '支付失败',
          className: 'bg-red-100 text-red-800',
        }
      case 'CLOSED':
        return {
          icon: '🔒',
          text: '订单已关闭',
          className: 'bg-gray-100 text-gray-800',
        }
      case 'PENDING':
      default:
        return {
          icon: '⏳',
          text: '等待支付',
          className: 'bg-yellow-100 text-yellow-800',
        }
    }
  }

  const config = getStatusConfig()

  return (
    <div className="text-center">
      <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.className}`}>
        {showIcon && <span className="mr-1">{config.icon}</span>}
        {config.text}
      </div>
      {message && (
        <p className="text-sm text-gray-600 mt-2">{message}</p>
      )}
    </div>
  )
}

/**
 * 错误提示组件
 */
interface ErrorMessageProps {
  message: string
}

export function ErrorMessage({ message }: ErrorMessageProps) {
  return (
    <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
      <div className="flex">
        <div className="flex-shrink-0">
          <span className="text-red-400 text-lg">⚠</span>
        </div>
        <div className="ml-3">
          <p className="text-sm text-red-600">{message}</p>
        </div>
      </div>
    </div>
  )
} 